import { IS_DEV } from "@config/consts";
import { ZIP_SUGGESTION_ENDPOINT } from "@config/endpoints";

import { AsyncActionResult } from "@services/AsyncActionCreatorFactory";
import { ASYNC_ACTION_TYPES } from "@services/FetchFacade";
import { buildUrl } from "@services/BuildUrl";

import { asyncActionCreator } from "@app/services/AsyncActionCreator";

import {
  SuggestionResponse,
  FetchSuggestionsProps,
  GetSuggestionsProps,
  ZIP_CODE_ACTION,
} from "./ZipCodeField.types";

const fetchSuggestions = ({
  query,
  serviceType,
  signal,
}: FetchSuggestionsProps): Promise<AsyncActionResult<SuggestionResponse[]>> => {
  return asyncActionCreator(
    {
      action: "",
      method: ASYNC_ACTION_TYPES.GET,
      url: buildUrl(ZIP_SUGGESTION_ENDPOINT)
        .setQuery("q", query.trim())
        .setQuery("type", serviceType)
        .done(),
    },
    {
      signal,
    },
  );
};

/**
 * @returns `true` if the zip code is covered by selected cleaning service, `false` otherwise.
 */
export const getSuggestions = async ({
  query,
  serviceType,
  dispatch,
}: GetSuggestionsProps): Promise<boolean> => {
  let isCoverage = false;

  dispatch({ type: ZIP_CODE_ACTION.FETCH_SUGGESTIONS });

  if (!query.trim()) {
    dispatch({ type: ZIP_CODE_ACTION.SET_SUGGESTIONS, payload: [] });
    return isCoverage;
  }

  try {
    const controller = new AbortController();
    const { signal } = controller;

    const { onSuccess, onError } = await fetchSuggestions({
      query,
      serviceType,
      signal,
    });

    onSuccess(({ data }) => {
      const suggestions = data.map(suggestion => ({
        value: suggestion.zip,
        label: suggestion.hideCityName
          ? suggestion.zip
          : `${suggestion.zip} - ${suggestion.city}`,
      }));

      isCoverage = suggestions.some(suggestion => suggestion.value === query);
      dispatch({ type: ZIP_CODE_ACTION.SET_SUGGESTIONS, payload: suggestions });
    });

    onError(errors => {
      console.error(errors);
      dispatch({ type: ZIP_CODE_ACTION.FAILED_SUGGESTIONS });
    });
  } catch (error) {
    /**
     * Intentional silent errors.
     * Zip code suggestions are an optional feature.
     * We don't want to spam with error toasts on the Homepage if these requests fail.
     */
    IS_DEV && error?.name !== "AbortError" && console.error(error);
    dispatch({ type: ZIP_CODE_ACTION.FAILED_SUGGESTIONS });
  }

  return isCoverage;
};
